import { asCuePreviewData } from "@app/cuePreview";
import AffiliateShopping from "@app/routes/AffiliateShopping/AffiliateShopping";
import Category from "@app/routes/Category/Category";
import { StoryPage } from "@app/routes/Story/Story";
import {
  AffiliateShoppingData,
  SectionData,
  StoryData,
} from "@app/types/ServerCustomContext";
import { extractUrlRedirect } from "@app/utils/extractUrlRedirect";
import MetaData from "@components/Modules/MetaData";
import {
  isAffiliateShoppingData,
  isStoryData,
  mapErrorDataToMetaData,
  mapSectionDataToMetaData,
  mapStaticContentDataToMetaData,
  mapStoryDataToMetaData,
} from "@components/Modules/MetaData/mappers";
import { Route } from "@route-types/StoryOrSubsection/+types/StoryOrSubsection";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { LoaderFunctionArgs, useLoaderData } from "react-router";

import StoryOrSubsectionData from "./StoryOrSubSection.server";

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<
    (SectionData | StoryData | AffiliateShoppingData) & GlobalBaseData,
    string
  >;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  const payload = loaderData.payload;
  let metaData;

  if (isStoryData(payload)) {
    metaData = mapStoryDataToMetaData(payload);
  } else if (isAffiliateShoppingData(payload)) {
    metaData = mapStaticContentDataToMetaData(payload);
  } else {
    metaData = mapSectionDataToMetaData(payload);
  }

  return MetaData(metaData);
};

export default function StoryOrSubsection() {
  const response: TRouteWithoutRedirect<
    SectionData | StoryData | null,
    string
  > = useLoaderData();

  if (response.type === ResponseType.SUCCESS) {
    const payload = response.payload;

    if (isStoryData(payload)) {
      return <StoryPage />;
    }

    if (
      payload &&
      "template" in payload &&
      payload.template === "affiliateShopping"
    ) {
      return <AffiliateShopping />;
    }
  }

  return <Category />;
}

export async function loader({
  params,
  context,
  request,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<
    StoryData | AffiliateShoppingData | SectionData | GlobalBaseData,
    GlobalBaseData | string
  >
> {
  const data = await StoryOrSubsectionData(
    params,
    asCuePreviewData(context.previewData),
  );

  if (data.type === ResponseType.CLIENT_ERROR && data.statusCode === 404) {
    const redirectResult = await extractUrlRedirect(request.url);

    if (redirectResult && redirectResult.target) {
      return redirectResult;
    }
  }

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}

/**
 * Client-only loader to prevent /storyRoute.data endpoint requests.
 *
 * React Router's default behavior on client-side navigation (via useNavigate):
 * - Attempts to fetch data from /storyRoute.data endpoint
 * - Results in 404 errors (endpoint doesn't exist)
 *
 * By exporting clientLoader:
 * - No requests are made to /storyRoute.data endpoint
 * - Data returned from /context.json
 * - Component handles all data fetching via useSWRInfinite
 */
export async function clientLoader({
  ...args
}: LoaderFunctionArgs & {
  serverLoader: <T = unknown>() => Promise<T>;
}) {
  const result = await fetchContext(args);

  if (
    !result ||
    typeof result !== "object" ||
    !("context" in result) ||
    !result.context
  ) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  return result.context;
}
